import { useEffect, useMemo, useState } from 'react'
import { Box, Flex, Input, Spinner, Text } from '@chakra-ui/react'
import { LuLock, LuArrowLeft, LuSave, LuSearch, LuCheck, LuCircle, LuLayers } from 'react-icons/lu'
import { useNavigate, useParams } from 'react-router-dom'
import { PageHeader, toaster } from '@minetrace/ui'
import { themeColors, brand } from '../../../theme'
import {
  permissionsService,
  type PermissionResponse,
  type RolePermissionsResponse,
} from '../../../api/services/permissions.service'
import { useHasPermission } from '../../../auth/PermissionContext'

interface ModuleGroup {
  module: string
  items: PermissionResponse[]
}

function groupByModule(items: PermissionResponse[]): ModuleGroup[] {
  const map = new Map<string, PermissionResponse[]>()
  for (const item of items) {
    const list = map.get(item.module) ?? []
    list.push(item)
    map.set(item.module, list)
  }
  return Array.from(map.entries())
    .map(([module, items]) => ({ module, items }))
    .sort((a, b) => a.module.localeCompare(b.module))
}

// Special tabs that aren't tied to a single module — they sit at the top of
// the sidebar above the per-module list. "all" is the cross-cutting view
// (search + everything); "assigned" is the audit view (only what's checked).
type SpecialTab = 'all' | 'assigned'
type ActiveTab = SpecialTab | { module: string }

export default function RolePermissionsAdminPage() {
  const colors = themeColors
  const navigate = useNavigate()
  const { id: roleId } = useParams<{ id: string }>()
  const allowed = useHasPermission('roles.assignPermissions')

  const [allPerms, setAllPerms] = useState<PermissionResponse[]>([])
  const [roleData, setRoleData] = useState<RolePermissionsResponse | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [activeTab, setActiveTab] = useState<ActiveTab>('all')

  useEffect(() => {
    if (!allowed || !roleId) return
    let cancelled = false
    setLoading(true)
    Promise.all([
      permissionsService.list(),
      permissionsService.getForRole(roleId),
    ])
      .then(([perms, role]) => {
        if (cancelled) return
        setAllPerms(perms)
        setRoleData(role)
        setSelected(new Set(role.assignedPermissionIds))
      })
      .catch(() => { if (!cancelled) setError('No se pudo cargar la matriz de permisos') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [allowed, roleId])

  // All groups, unfiltered — used to drive the sidebar tabs.
  const allGroups = useMemo(() => groupByModule(allPerms), [allPerms])

  // What lives in the right pane right now, filtered by search + active tab.
  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase()
    let pool: PermissionResponse[]
    if (activeTab === 'all') {
      pool = allPerms
    } else if (activeTab === 'assigned') {
      pool = allPerms.filter(p => selected.has(p.id))
    } else {
      pool = allPerms.filter(p => p.module === activeTab.module)
    }
    if (!q) return pool
    return pool.filter(p =>
      p.code.toLowerCase().includes(q) ||
      p.displayName.toLowerCase().includes(q) ||
      p.module.toLowerCase().includes(q),
    )
  }, [allPerms, activeTab, search, selected])

  // For the right pane, group whatever is visible by module so admins
  // always see the structural context (a perm under USERS reads as
  // USERS / users.create rather than as an unmoored row).
  const visibleGroups = useMemo(() => groupByModule(visibleItems), [visibleItems])

  const togglePermission = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = (items: PermissionResponse[], allOn: boolean) => {
    setSelected(prev => {
      const next = new Set(prev)
      for (const item of items) {
        if (allOn) next.delete(item.id)
        else next.add(item.id)
      }
      return next
    })
  }

  const dirty = useMemo(() => {
    if (!roleData) return false
    const original = new Set(roleData.assignedPermissionIds)
    if (original.size !== selected.size) return true
    for (const id of selected) if (!original.has(id)) return true
    return false
  }, [roleData, selected])

  const handleSave = async () => {
    if (!roleId || !roleData) return
    setSaving(true)
    try {
      const updated = await permissionsService.updateForRole(roleId, {
        permissionIds: Array.from(selected),
      })
      setRoleData(updated)
      setSelected(new Set(updated.assignedPermissionIds))
      toaster.success({ description: 'Permisos actualizados' })
    } catch {
      // toast already handled by api client
    } finally {
      setSaving(false)
    }
  }

  if (!allowed) {
    return (
      <Box p={8}>
        <Flex alignItems="center" gap={3} color={colors.textMuted}>
          <LuLock size={18} />
          <Text>No tiene permiso para asignar permisos a roles.</Text>
        </Flex>
      </Box>
    )
  }

  const totalAssigned = selected.size
  const totalPerms = allPerms.length

  const isActiveTab = (tab: ActiveTab): boolean => {
    if (typeof activeTab === 'string') return typeof tab === 'string' && tab === activeTab
    if (typeof tab === 'string') return false
    return tab.module === activeTab.module
  }

  return (
    <Box>
      <PageHeader
        title={roleData ? `Permisos de ${roleData.roleName}` : 'Permisos del rol'}
        subtitle={
          roleData
            ? `${roleData.tenantId ? 'Rol del tenant' : 'Rol del sistema'} · código: ${roleData.roleCode} · ${totalAssigned} de ${totalPerms} permisos asignados`
            : 'Cargando…'
        }
        colors={colors}
      />

      <Flex gap={2} mb={4} alignItems="center" flexWrap="wrap">
        <Box
          as="button"
          display="inline-flex"
          alignItems="center"
          gap={1.5}
          px={3}
          py={2}
          borderRadius="md"
          border="1px solid"
          borderColor={colors.border}
          color={colors.textMuted}
          cursor="pointer"
          fontSize="sm"
          _hover={{ bg: colors.bgHover }}
          onClick={() => navigate('/app/maestros/roles')}
        >
          <LuArrowLeft size={14} />
          <Text>Volver a roles</Text>
        </Box>

        <Box flex="1" />

        {dirty && (
          <Flex alignItems="center" gap={1.5} px={3} py={1.5} borderRadius="md" bg="rgba(212, 154, 42, 0.12)" color="#8a5f0b">
            <LuCircle size={8} fill="currentColor" />
            <Text fontSize="xs" fontWeight="600">Cambios sin guardar</Text>
          </Flex>
        )}

        <Box
          as="button"
          display="inline-flex"
          alignItems="center"
          gap={1.5}
          px={4}
          py={2}
          borderRadius="md"
          bg={dirty ? brand.green : colors.bgCard}
          color={dirty ? 'white' : colors.textDimmed}
          cursor={dirty && !saving ? 'pointer' : 'not-allowed'}
          fontSize="sm"
          fontWeight="600"
          opacity={dirty && !saving ? 1 : 0.7}
          _hover={dirty && !saving ? { opacity: 0.9 } : {}}
          onClick={() => dirty && !saving && handleSave()}
        >
          <LuSave size={14} />
          <Text>{saving ? 'Guardando…' : 'Guardar cambios'}</Text>
        </Box>
      </Flex>

      {loading && (
        <Flex justifyContent="center" py={10}><Spinner color={brand.green} /></Flex>
      )}

      {error && !loading && (
        <Text color="red.500">{error}</Text>
      )}

      {!loading && !error && roleData && (
        <Flex
          gap={4}
          alignItems="flex-start"
          border="1px solid"
          borderColor={colors.border}
          borderRadius="xl"
          bg={colors.bgPanel}
          overflow="hidden"
          minH="500px"
        >
          {/* ── Left: module sidebar (tabs) ────────────────────────────── */}
          <Box
            flex="0 0 240px"
            borderRight="1px solid"
            borderColor={colors.border}
            bg={colors.bgCard}
            py={2}
            alignSelf="stretch"
          >
            <SidebarTab
              label="Todos"
              icon={<LuLayers size={14} />}
              count={`${totalAssigned}/${totalPerms}`}
              active={isActiveTab('all')}
              colors={colors}
              onClick={() => setActiveTab('all')}
            />
            <SidebarTab
              label="Solo asignados"
              icon={<LuCheck size={14} />}
              count={`${totalAssigned}`}
              active={isActiveTab('assigned')}
              colors={colors}
              onClick={() => setActiveTab('assigned')}
            />

            <Box my={2} mx={3} h="1px" bg={colors.border} />

            <Text
              px={3}
              pb={1.5}
              fontSize="2xs"
              fontWeight="700"
              color={colors.textDimmed}
              textTransform="uppercase"
              letterSpacing="0.05em"
            >
              Por módulo
            </Text>
            {allGroups.map(g => {
              const sel = g.items.filter(i => selected.has(i.id)).length
              return (
                <SidebarTab
                  key={g.module}
                  label={g.module}
                  count={`${sel}/${g.items.length}`}
                  active={isActiveTab({ module: g.module })}
                  colors={colors}
                  highlight={sel > 0}
                  onClick={() => setActiveTab({ module: g.module })}
                />
              )
            })}
          </Box>

          {/* ── Right: content pane ────────────────────────────────────── */}
          <Box flex="1" minW={0} p={4}>
            <Flex alignItems="center" gap={2} mb={3} flexWrap="wrap">
              <Box position="relative" flex="1" maxW="360px">
                <Box position="absolute" left={2.5} top="50%" transform="translateY(-50%)" pointerEvents="none">
                  <LuSearch size={14} color={colors.textMuted} />
                </Box>
                <Input
                  pl={8}
                  placeholder="Buscar permiso…"
                  value={search}
                  onChange={e => setSearch(e.target.value.slice(0, 200))}
                  maxLength={200}
                />
              </Box>
              <Box flex="1" />
              {visibleItems.length > 0 && (() => {
                const allOn = visibleItems.every(p => selected.has(p.id))
                return (
                  <Box
                    as="button"
                    fontSize="xs"
                    color={brand.green}
                    cursor="pointer"
                    px={2}
                    py={1}
                    _hover={{ textDecoration: 'underline' }}
                    onClick={() => toggleAll(visibleItems, allOn)}
                  >
                    {allOn ? 'Quitar visibles' : 'Marcar visibles'} ({visibleItems.length})
                  </Box>
                )
              })()}
            </Flex>

            {visibleGroups.length === 0 ? (
              <Flex
                direction="column"
                alignItems="center"
                justifyContent="center"
                py={10}
                color={colors.textMuted}
                gap={2}
              >
                <LuSearch size={28} style={{ opacity: 0.4 }} />
                <Text fontSize="sm">
                  {activeTab === 'assigned'
                    ? 'Este rol aún no tiene permisos asignados.'
                    : 'Sin resultados para esta búsqueda.'}
                </Text>
              </Flex>
            ) : (
              <Box display="flex" flexDirection="column" gap={4}>
                {visibleGroups.map(group => {
                  const sel = group.items.filter(i => selected.has(i.id)).length
                  const allOn = sel === group.items.length
                  // When the user is already viewing a single module, the
                  // module header in the right pane would be redundant —
                  // collapse it to a thinner divider.
                  const compact = typeof activeTab === 'object' && activeTab.module === group.module
                  return (
                    <Box key={group.module}>
                      {!compact && (
                        <Flex alignItems="center" gap={3} px={1} pb={2}>
                          <Text
                            fontSize="2xs"
                            fontWeight="700"
                            textTransform="uppercase"
                            letterSpacing="0.05em"
                            color={colors.textMuted}
                          >
                            {group.module}
                          </Text>
                          <Text fontSize="2xs" color={colors.textDimmed}>
                            {sel}/{group.items.length}
                          </Text>
                          <Box flex="1" />
                          <Box
                            as="button"
                            fontSize="2xs"
                            color={brand.green}
                            cursor="pointer"
                            _hover={{ textDecoration: 'underline' }}
                            onClick={() => toggleAll(group.items, allOn)}
                          >
                            {allOn ? 'Quitar todo' : 'Marcar todo'}
                          </Box>
                        </Flex>
                      )}
                      <Box border="1px solid" borderColor={colors.border} borderRadius="lg" overflow="hidden">
                        {group.items.map((p, idx) => {
                          const checked = selected.has(p.id)
                          return (
                            <Flex
                              key={p.id}
                              as="label"
                              alignItems="center"
                              px={3}
                              py={2.5}
                              gap={3}
                              borderTop={idx === 0 ? 'none' : '1px solid'}
                              borderColor={colors.border}
                              cursor="pointer"
                              bg={checked ? 'rgba(42, 138, 135, 0.04)' : 'transparent'}
                              _hover={{ bg: checked ? 'rgba(42, 138, 135, 0.08)' : colors.bgHover }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => togglePermission(p.id)}
                                style={{ accentColor: brand.green, width: 16, height: 16, flexShrink: 0 }}
                              />
                              <Box flex="0 0 220px" minW={0}>
                                <Text fontSize="sm" fontWeight="500" color={colors.text} truncate>
                                  {p.displayName}
                                </Text>
                                <Text fontSize="2xs" fontFamily="mono" color={colors.textDimmed} truncate>
                                  {p.code}
                                </Text>
                              </Box>
                              <Box flex="1" minW={0}>
                                <Text fontSize="xs" color={colors.textMuted} truncate>{p.description ?? '—'}</Text>
                              </Box>
                            </Flex>
                          )
                        })}
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            )}
          </Box>
        </Flex>
      )}
    </Box>
  )
}

// ─── Sidebar tab (module / "Todos" / "Asignados") ───────────────────────────

interface SidebarTabProps {
  label: string
  icon?: React.ReactNode
  count: string
  active: boolean
  highlight?: boolean
  colors: typeof themeColors
  onClick: () => void
}

function SidebarTab({ label, icon, count, active, highlight, colors, onClick }: SidebarTabProps) {
  return (
    <Flex
      as="button"
      alignItems="center"
      gap={2}
      w="100%"
      px={3}
      py={2}
      cursor="pointer"
      bg={active ? 'rgba(42, 138, 135, 0.08)' : 'transparent'}
      color={active ? brand.green : colors.text}
      borderLeft="3px solid"
      borderColor={active ? brand.green : 'transparent'}
      fontWeight={active ? '600' : '500'}
      _hover={active ? {} : { bg: colors.bgHover }}
      onClick={onClick}
      transition="all 0.1s"
      textAlign="left"
    >
      {icon && <Box flexShrink={0} opacity={active ? 1 : 0.6}>{icon}</Box>}
      <Text fontSize="sm" flex="1" truncate>{label}</Text>
      <Text
        fontSize="2xs"
        fontFamily="mono"
        color={active ? brand.green : highlight ? colors.text : colors.textDimmed}
        fontWeight={highlight ? '600' : '500'}
      >
        {count}
      </Text>
    </Flex>
  )
}
